import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Combobox, Input, Select } from '@/components/ui';
import {
  addCaseEntry,
  addCaseMember,
  getWarningAdvice,
  issueWarningLetter,
  runCaseCommand,
  type AppealDecision,
  type GrievanceActionInput,
  type GrievanceFinding,
  type IssueFindingsInput,
  type DecideAppealInput,
  type DecideDisciplineInput,
  type DisciplineDecision,
  type HrCase,
  type HrCaseMemberRole,
  type ScheduleHearingInput,
} from '@/api/services/hrCases.service';
import { listTemplates } from '@/api/services/hrDocuments.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { FormShell, TextArea } from './caseFields';
import {
  APPEAL_LABELS,
  DECISION_LABELS,
  DISCIPLINE_ROLES,
  FINDING_LABELS,
  GRIEVANCE_ROLES,
  LABEL_CLASS,
  ROLE_LABELS,
  toDateTimeInput,
} from './caseFormat';
import { useCaseCommand } from './useCaseCommand';
import { searchCompanyPeople } from './casePeople';

export type CaseAction =
  | 'hearing'
  | 'decide'
  | 'withdraw'
  | 'letter'
  | 'member'
  | 'entry'
  | 'suspend'
  | 'reinstate'
  | 'appeal'
  | 'acknowledge'
  | 'findings';

interface FormProps {
  companyId: string;
  hrCase: HrCase;
  onDone: () => void;
}

export function HearingForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const noticeDays = hrCase.settings.hearingNoticeDays;
  // Fixed when the form opens so validation is stable across renders.
  const [openedAt] = useState(() => Date.now());
  const [at, setAt] = useState(() =>
    toDateTimeInput(new Date(openedAt + (noticeDays + 1) * 86_400_000))
  );
  const [location, setLocation] = useState('');
  const [subjectSummary, setSubjectSummary] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: Omit<ScheduleHearingInput, 'requestId'>) =>
      runCaseCommand(companyId, hrCase._id, {
        command: 'schedule-hearing',
        input: { ...input, requestId },
      }),
    'Hearing scheduled',
    onDone
  );
  const when = new Date(at);
  const valid = !Number.isNaN(when.getTime()) && when.getTime() > openedAt;
  const shortNotice = valid && when.getTime() < openedAt + noticeDays * 86_400_000;
  const ready =
    valid && subjectSummary.trim() !== '' && (!shortNotice || overrideReason.trim() !== '');

  return (
    <FormShell
      title={hrCase.deadlines.hearingAt ? 'Reschedule hearing' : 'Schedule hearing'}
      error={command.error}
      onCancel={onDone}
      onSubmit={() =>
        command.submit({
          at: when.toISOString(),
          location: location.trim() || undefined,
          subjectSummary: subjectSummary.trim(),
          overrideReason: shortNotice ? overrideReason.trim() : undefined,
        })
      }
      submitLabel="Schedule"
      disabled={!ready}
      isPending={command.isPending}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          id="case-hearing-at"
          label="Date and time"
          type="datetime-local"
          value={at}
          error={at && !valid ? 'Choose a time in the future.' : undefined}
          hint={`Company notice: ${noticeDays} day${noticeDays === 1 ? '' : 's'}.`}
          onChange={(event) => setAt(event.target.value)}
        />
        <Input
          id="case-hearing-location"
          label="Location"
          value={location}
          onChange={(event) => setLocation(event.target.value)}
        />
      </div>
      <TextArea
        id="case-hearing-summary"
        label="Invitation shared with the employee"
        value={subjectSummary}
        onChange={setSubjectSummary}
        required
        hint="The employee sees this text, the date, and the location."
      />
      {shortNotice && (
        <TextArea
          id="case-hearing-override"
          label="Reason for short notice"
          value={overrideReason}
          onChange={setOverrideReason}
          required
          hint={`This is sooner than the ${noticeDays}-day notice; the reason is kept on the case record.`}
        />
      )}
    </FormShell>
  );
}

export function DecideForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const levels = hrCase.settings.warningLevels;
  const [decision, setDecision] = useState<DisciplineDecision>('warning');
  const [warningLevelKey, setWarningLevelKey] = useState(levels[0]?.key ?? '');
  const [reason, setReason] = useState('');
  const [subjectSummary, setSubjectSummary] = useState('');
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const advice = useQuery({
    queryKey: ['hr-case', companyId, hrCase._id, 'advice'],
    queryFn: () => getWarningAdvice(companyId, hrCase._id),
  });
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: Omit<DecideDisciplineInput, 'requestId'>) =>
      runCaseCommand(companyId, hrCase._id, { command: 'decide', input: { ...input, requestId } }),
    'Decision recorded',
    onDone
  );
  const suggested = levels.find((level) => level.key === advice.data?.suggestedLevelKey);
  const ready =
    reason.trim() !== '' &&
    subjectSummary.trim() !== '' &&
    (decision !== 'warning' || warningLevelKey !== '') &&
    (decision !== 'dismissal' || lastWorkingDate !== '');

  return (
    <FormShell
      title="Record decision"
      error={command.error}
      onCancel={onDone}
      onSubmit={() =>
        command.submit({
          decision,
          ...(decision === 'warning' && { warningLevelKey }),
          reason: reason.trim(),
          subjectSummary: subjectSummary.trim(),
          ...(decision === 'dismissal' && {
            lastWorkingDate: new Date(`${lastWorkingDate}T00:00:00`).toISOString(),
          }),
        })
      }
      submitLabel="Record decision"
      submitVariant={decision === 'dismissal' ? 'danger' : 'primary'}
      disabled={!ready}
      isPending={command.isPending}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id="case-decision"
          label="Decision"
          value={decision}
          onChange={(value) => setDecision(value as DisciplineDecision)}
          options={(Object.keys(DECISION_LABELS) as DisciplineDecision[]).map((value) => ({
            value,
            label: DECISION_LABELS[value],
          }))}
          clearable={false}
        />
        {decision === 'warning' && (
          <Select
            id="case-warning-level"
            label="Warning level"
            value={warningLevelKey}
            onChange={setWarningLevelKey}
            options={levels.map((level) => ({
              value: level.key,
              label: `${level.label} · expires after ${level.expiryDays} days`,
            }))}
            clearable={false}
          />
        )}
        {decision === 'dismissal' && (
          <Input
            id="case-last-working-date"
            label="Last working date"
            type="date"
            value={lastWorkingDate}
            onChange={(event) => setLastWorkingDate(event.target.value)}
            hint="The employment moves into notice."
          />
        )}
      </div>
      {decision === 'warning' && (
        <div
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
          aria-live="polite"
        >
          {advice.isLoading ? (
            <p className="text-gray-600 dark:text-gray-300">Checking active warnings…</p>
          ) : advice.error ? (
            <p className="text-gray-600 dark:text-gray-300">
              Active warnings unavailable: {getErrorMessage(advice.error)}
            </p>
          ) : (
            <>
              <p className="text-gray-700 dark:text-gray-200">
                {advice.data?.activeWarnings.length
                  ? `${advice.data.activeWarnings.length} active warning${advice.data.activeWarnings.length === 1 ? '' : 's'} on cases you can see.`
                  : 'No active warnings on cases you can see.'}
                {suggested && ` Suggested level: ${suggested.label}.`}
              </p>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                A suggestion only; the decision is yours.
              </p>
            </>
          )}
        </div>
      )}
      <TextArea
        id="case-decision-reason"
        label="Reasons (case team only)"
        value={reason}
        onChange={setReason}
        required
        rows={4}
      />
      <TextArea
        id="case-decision-summary"
        label="Outcome shared with the employee"
        value={subjectSummary}
        onChange={setSubjectSummary}
        required
        hint={`The employee may appeal within ${hrCase.settings.appealWindowDays} days.`}
      />
    </FormShell>
  );
}

export function WithdrawForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const [reason, setReason] = useState('');
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: { reason: string }) =>
      runCaseCommand(companyId, hrCase._id, {
        command: 'withdraw',
        input: { requestId, reason: input.reason },
      }),
    'Case withdrawn',
    onDone
  );
  return (
    <FormShell
      title="Withdraw case"
      error={command.error}
      onCancel={onDone}
      onSubmit={() => command.submit({ reason: reason.trim() })}
      submitLabel="Withdraw"
      submitVariant="danger"
      disabled={reason.trim() === ''}
      isPending={command.isPending}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        The employee is told the matter was withdrawn. The reason stays with the case team.
      </p>
      <TextArea
        id="case-withdraw-reason"
        label="Reason"
        value={reason}
        onChange={setReason}
        required
      />
    </FormShell>
  );
}

export function WarningLetterForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const [templateId, setTemplateId] = useState('');
  const templates = useQuery({
    queryKey: ['hr-document-templates', companyId, 'warning'],
    queryFn: () => listTemplates(companyId),
  });
  const options = (Array.isArray(templates.data) ? templates.data : [])
    .filter((template) => template.type === 'warning' && !template.archived && template.currentVersion)
    .map((template) => ({ value: template._id, label: template.name }));
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: { templateId: string }) =>
      issueWarningLetter(companyId, hrCase._id, { requestId, ...input }),
    'Warning letter issued for signature',
    onDone
  );
  return (
    <FormShell
      title="Issue warning letter"
      error={command.error}
      onCancel={onDone}
      onSubmit={() => command.submit({ templateId })}
      submitLabel="Issue letter"
      disabled={templateId === ''}
      isPending={command.isPending}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        The employee signs it in My Letters. The letter stays out of the Documents tab and belongs
        to this case.
      </p>
      {templates.error ? (
        <p className="text-sm text-danger" role="alert">
          Templates unavailable: {getErrorMessage(templates.error)}. Issuing letters also needs the
          documents permission.
        </p>
      ) : options.length === 0 && !templates.isLoading ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          No approved warning template exists yet. Create one in the Documents tab.
        </p>
      ) : (
        <Select
          id="case-letter-template"
          label="Warning template"
          value={templateId}
          onChange={setTemplateId}
          options={options}
          placeholder={templates.isLoading ? 'Loading templates…' : 'Choose a template'}
          clearable={false}
        />
      )}
    </FormShell>
  );
}

export function AddMemberForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const [userId, setUserId] = useState('');
  const grievance = hrCase.kind === 'grievance';
  const roles = grievance ? GRIEVANCE_ROLES : DISCIPLINE_ROLES;
  const [role, setRole] = useState<HrCaseMemberRole>(
    grievance ? (hrCase.status === 'blocked' ? 'handler' : 'investigator') : 'decision_maker'
  );
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: { userId: string; role: HrCaseMemberRole }) =>
      addCaseMember(companyId, hrCase._id, { requestId, ...input }),
    'Team member added',
    onDone
  );
  return (
    <FormShell
      title="Add to case team"
      error={command.error}
      onCancel={onDone}
      onSubmit={() => command.submit({ userId, role })}
      submitLabel="Add"
      disabled={userId === ''}
      isPending={command.isPending}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {grievance
          ? 'Members need the grievances permission and access to this company. The employee who raised it and the person it names can never join, and a handler cannot hear the appeal.'
          : 'Members need the discipline permission and access to this company. The employee concerned can never join, the investigator cannot decide, and neither can hear the appeal.'}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Combobox
          id="case-member-user"
          label="Person"
          placeholder="Search by name or employee ID"
          value={userId}
          onChange={setUserId}
          loadOptions={searchCompanyPeople(companyId)}
        />
        <Select
          id="case-member-role"
          label="Role"
          value={role}
          onChange={(value) => setRole(value as HrCaseMemberRole)}
          options={roles.map((value) => ({ value, label: ROLE_LABELS[value] }))}
          clearable={false}
        />
      </div>
    </FormShell>
  );
}

export function AddEntryForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const [type, setType] = useState<'note' | 'meeting'>('note');
  const [audience, setAudience] = useState<'team' | 'complainant' | 'respondent' | 'parties'>(
    'team'
  );
  const [body, setBody] = useState('');
  const [meetingAt, setMeetingAt] = useState('');
  const [location, setLocation] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: Omit<Parameters<typeof addCaseEntry>[2], 'requestId'>) =>
      addCaseEntry(companyId, hrCase._id, { requestId, ...input }),
    'Added to the case record',
    onDone
  );
  const ready = body.trim() !== '' && (type !== 'meeting' || meetingAt !== '');
  const subjectName = hrCase.subject?.name ?? 'the employee';
  const audienceOptions =
    hrCase.kind === 'grievance'
      ? [
          { value: 'team', label: 'Case team only' },
          {
            value: 'complainant',
            label: `Case team and ${hrCase.complainant?.name ?? 'the employee who raised it'}`,
          },
          ...(hrCase.subject
            ? [{ value: 'respondent', label: `Case team and ${hrCase.subject.name}` }]
            : []),
        ]
      : [
          { value: 'team', label: 'Case team only' },
          { value: 'parties', label: `Case team and ${subjectName}` },
        ];

  return (
    <FormShell
      title="Add to record"
      error={command.error}
      onCancel={onDone}
      onSubmit={() =>
        command.submit({
          type,
          audience: audience === 'team' ? 'team' : 'parties',
          ...((audience === 'complainant' || audience === 'respondent') && {
            addressee: audience,
          }),
          body: body.trim(),
          ...(type === 'meeting' && {
            meetingAt: new Date(meetingAt).toISOString(),
            location: location.trim() || undefined,
          }),
          ...(file && { file }),
        })
      }
      submitLabel="Add"
      disabled={!ready}
      isPending={command.isPending}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id="case-entry-type"
          label="Entry"
          value={type}
          onChange={(value) => setType(value as 'note' | 'meeting')}
          options={[
            { value: 'note', label: 'Note or evidence' },
            { value: 'meeting', label: 'Meeting' },
          ]}
          clearable={false}
        />
        <Select
          id="case-entry-audience"
          label="Who can read it"
          value={audience}
          onChange={(value) => setAudience(value as typeof audience)}
          options={audienceOptions}
          clearable={false}
        />
      </div>
      {type === 'meeting' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            id="case-entry-meeting-at"
            label="Date and time"
            type="datetime-local"
            value={meetingAt}
            onChange={(event) => setMeetingAt(event.target.value)}
          />
          <Input
            id="case-entry-location"
            label="Location"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />
        </div>
      )}
      <TextArea id="case-entry-body" label="Details" value={body} onChange={setBody} required />
      <div>
        <label htmlFor="case-entry-file" className={LABEL_CLASS}>
          Evidence file (optional)
        </label>
        <input
          id="case-entry-file"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx,.xlsx"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium dark:text-gray-200 dark:file:bg-gray-700"
          aria-describedby="case-entry-file-hint"
        />
        <p id="case-entry-file-hint" className="mt-1.5 text-xs text-gray-600 dark:text-gray-300">
          PDF, image, text, Word, or Excel, up to 20 MB. The file is stored privately and its hash
          is recorded.
        </p>
      </div>
    </FormShell>
  );
}

/**
 * Suspends the employee pending investigation, or lifts a suspension this
 * case started. The employment history records a neutral reason; the reason
 * given here stays with the case team.
 */
export function SuspensionForm({
  companyId,
  hrCase,
  onDone,
  mode,
}: FormProps & { mode: 'suspend' | 'reinstate' }): React.ReactElement {
  const [reason, setReason] = useState('');
  const suspend = mode === 'suspend';
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: { reason: string }) =>
      runCaseCommand(companyId, hrCase._id, {
        command: suspend ? 'suspend-subject' : 'reinstate-subject',
        input: { requestId, reason: input.reason },
      }),
    suspend ? 'Employee suspended' : 'Suspension lifted',
    onDone
  );
  const name = hrCase.subject?.name ?? 'The employee';
  return (
    <FormShell
      title={suspend ? 'Suspend pending investigation' : 'Lift suspension'}
      error={command.error}
      onCancel={onDone}
      onSubmit={() => command.submit({ reason: reason.trim() })}
      submitLabel={suspend ? 'Suspend' : 'Lift suspension'}
      submitVariant={suspend ? 'danger' : 'primary'}
      disabled={reason.trim() === ''}
      isPending={command.isPending}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {suspend
          ? `${name}'s employment in this company moves to suspended and they are told. Their employment history shows a neutral reason, not this case.`
          : `${name} returns to the status they held before the suspension, and they are told.`}
      </p>
      <TextArea
        id={`case-${mode}-reason`}
        label="Reason (case team only)"
        value={reason}
        onChange={setReason}
        required
      />
    </FormShell>
  );
}

/** Decides the employee's appeal. Offered only to the case appeal officer. */
export function AppealDecisionForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const current = hrCase.outcome;
  const grievance = hrCase.kind === 'grievance';
  const levels = hrCase.settings.warningLevels.filter(
    (level) => level.key !== current?.warning?.levelKey
  );
  const findings = (Object.keys(FINDING_LABELS) as GrievanceFinding[]).filter(
    (value) => value !== current?.finding
  );
  const [appealDecision, setAppealDecision] = useState<AppealDecision>('upheld');
  const [variedTo, setVariedTo] = useState(
    grievance ? (findings[0] ?? '') : (levels[0]?.key ?? 'no_action')
  );
  const [reason, setReason] = useState('');
  const [subjectSummary, setSubjectSummary] = useState('');
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: Omit<DecideAppealInput, 'requestId'>) =>
      runCaseCommand(companyId, hrCase._id, {
        command: 'record-appeal-decision',
        input: { ...input, requestId },
      }),
    'Appeal decided',
    onDone
  );
  const changes = appealDecision !== 'upheld';
  // A grievance appeal that changes the outcome always names the new finding.
  const needsTarget = grievance ? changes : appealDecision === 'varied';
  const dismissal = current?.decision === 'dismissal';
  const ready = reason.trim() !== '' && subjectSummary.trim() !== '' && (!needsTarget || variedTo !== '');

  return (
    <FormShell
      title="Decide appeal"
      error={command.error}
      onCancel={onDone}
      onSubmit={() =>
        command.submit({
          appealDecision,
          ...(grievance && changes && { finding: variedTo as GrievanceFinding }),
          ...(!grievance &&
            appealDecision === 'varied' &&
            variedTo !== 'no_action' && { warningLevelKey: variedTo }),
          reason: reason.trim(),
          subjectSummary: subjectSummary.trim(),
        })
      }
      submitLabel="Record appeal decision"
      disabled={!ready}
      isPending={command.isPending}
    >
      {hrCase.appeal && (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Grounds of appeal
          </p>
          <p className="mt-1 whitespace-pre-line break-words text-gray-900 dark:text-gray-100">
            {hrCase.appeal.grounds}
          </p>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id="case-appeal-decision"
          label="Appeal decision"
          value={appealDecision}
          onChange={(value) => setAppealDecision(value as AppealDecision)}
          options={(Object.keys(APPEAL_LABELS) as AppealDecision[]).map((value) => ({
            value,
            label: APPEAL_LABELS[value],
          }))}
          clearable={false}
        />
        {needsTarget && (
          <Select
            id="case-appeal-varied-to"
            label={grievance ? 'New finding' : 'Varied to'}
            value={variedTo}
            onChange={setVariedTo}
            options={
              grievance
                ? findings.map((value) => ({ value, label: FINDING_LABELS[value] }))
                : [
                    ...levels.map((level) => ({
                      value: level.key,
                      label: `${level.label} warning`,
                    })),
                    { value: 'no_action', label: 'No action' },
                  ]
            }
            clearable={false}
          />
        )}
      </div>
      {changes && !grievance && (
        <p className="text-sm text-gray-600 dark:text-gray-300" aria-live="polite">
          {dismissal
            ? 'The separation is cancelled and the employee returns to the status they held before notice.'
            : 'A varied warning keeps its original issue date.'}{' '}
          An unsigned warning letter is voided; issue a new one if a warning still stands.
        </p>
      )}
      <TextArea
        id="case-appeal-reason"
        label="Reasons (case team only)"
        value={reason}
        onChange={setReason}
        required
        rows={4}
      />
      <TextArea
        id="case-appeal-summary"
        label="Outcome shared with the employee"
        value={subjectSummary}
        onChange={setSubjectSummary}
        required
      />
    </FormShell>
  );
}

/** A handler confirms the grievance was received, before the deadline. */
export function AcknowledgeForm({ companyId, hrCase, onDone }: FormProps): React.ReactElement {
  const [message, setMessage] = useState('');
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: { message?: string }) =>
      runCaseCommand(companyId, hrCase._id, {
        command: 'acknowledge',
        input: { requestId, ...input },
      }),
    'Grievance acknowledged',
    onDone
  );
  return (
    <FormShell
      title="Acknowledge grievance"
      error={command.error}
      onCancel={onDone}
      onSubmit={() => command.submit({ message: message.trim() || undefined })}
      submitLabel="Acknowledge"
      disabled={false}
      isPending={command.isPending}
    >
      <TextArea
        id="case-acknowledge-message"
        label="Message to the employee (optional)"
        value={message}
        onChange={setMessage}
        hint="Without a message, they are told their grievance was received and is being looked into."
      />
    </FormShell>
  );
}

interface ActionDraft {
  description: string;
  ownerUserId: string;
  dueAt: string;
  openCase: boolean;
  caseTypeKey: string;
  caseSummary: string;
}

const EMPTY_ACTION: ActionDraft = {
  description: '',
  ownerUserId: '',
  dueAt: '',
  openCase: false,
  caseTypeKey: '',
  caseSummary: '',
};

/**
 * Records the findings, what the employee is told, and follow-up actions. An
 * action can open a disciplinary case about the person the grievance names,
 * for handlers who also hold the discipline permission.
 */
export function FindingsForm({
  companyId,
  hrCase,
  onDone,
  canOpenDiscipline,
  disciplineTypes,
}: FormProps & {
  canOpenDiscipline: boolean;
  disciplineTypes: Array<{ key: string; label: string }>;
}): React.ReactElement {
  const [finding, setFinding] = useState<GrievanceFinding>('upheld');
  const [reason, setReason] = useState('');
  const [complainantSummary, setComplainantSummary] = useState('');
  const [actions, setActions] = useState<ActionDraft[]>([]);
  const command = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId, input: Omit<IssueFindingsInput, 'requestId'>) =>
      runCaseCommand(companyId, hrCase._id, {
        command: 'issue-findings',
        input: { ...input, requestId },
      }),
    'Findings issued',
    onDone
  );
  const offerCase = canOpenDiscipline && Boolean(hrCase.subject);
  const update = (index: number, patch: Partial<ActionDraft>): void =>
    setActions((list) => list.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  // Case types load separately, so an untouched choice falls to the first one.
  const caseTypeOf = (action: ActionDraft): string =>
    action.caseTypeKey || disciplineTypes[0]?.key || '';
  const actionsReady = actions.every(
    (action) =>
      action.description.trim() !== '' &&
      (!action.openCase || (caseTypeOf(action) !== '' && action.caseSummary.trim() !== ''))
  );
  const ready = reason.trim() !== '' && complainantSummary.trim() !== '' && actionsReady;

  return (
    <FormShell
      title="Issue findings"
      error={command.error}
      onCancel={onDone}
      onSubmit={() =>
        command.submit({
          finding,
          reason: reason.trim(),
          complainantSummary: complainantSummary.trim(),
          actions: actions.map(
            (action): GrievanceActionInput => ({
              description: action.description.trim(),
              ...(action.ownerUserId && { ownerUserId: action.ownerUserId }),
              ...(action.dueAt && {
                dueAt: new Date(`${action.dueAt}T00:00:00`).toISOString(),
              }),
              ...(action.openCase && {
                openDisciplineCase: {
                  typeKey: caseTypeOf(action),
                  summary: action.caseSummary.trim(),
                },
              }),
            })
          ),
        })
      }
      submitLabel="Issue findings"
      disabled={!ready}
      isPending={command.isPending}
    >
      <Select
        id="case-finding"
        label="Finding"
        value={finding}
        onChange={(value) => setFinding(value as GrievanceFinding)}
        options={(Object.keys(FINDING_LABELS) as GrievanceFinding[]).map((value) => ({
          value,
          label: FINDING_LABELS[value],
        }))}
        clearable={false}
      />
      <TextArea
        id="case-finding-reason"
        label="Reasons (case team only)"
        value={reason}
        onChange={setReason}
        required
        rows={4}
      />
      <TextArea
        id="case-finding-summary"
        label="Outcome shared with the employee who raised it"
        value={complainantSummary}
        onChange={setComplainantSummary}
        required
        hint={`They may appeal within ${hrCase.settings.appealWindowDays} days.`}
      />
      <fieldset className="space-y-3">
        <legend className={LABEL_CLASS}>Actions (case team only)</legend>
        {actions.map((action, index) => (
          <div
            key={index}
            className="space-y-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
          >
            <Input
              id={`case-action-${index}-description`}
              label={`Action ${index + 1}`}
              value={action.description}
              onChange={(event) => update(index, { description: event.target.value })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Combobox
                id={`case-action-${index}-owner`}
                label="Owner (optional)"
                placeholder="Search by name or employee ID"
                value={action.ownerUserId}
                onChange={(value) => update(index, { ownerUserId: value })}
                loadOptions={searchCompanyPeople(companyId)}
              />
              <Input
                id={`case-action-${index}-due`}
                label="Due (optional)"
                type="date"
                value={action.dueAt}
                onChange={(event) => update(index, { dueAt: event.target.value })}
              />
            </div>
            {offerCase && (
              <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={action.openCase}
                  onChange={(event) =>
                    update(index, {
                      openCase: event.target.checked,
                    })
                  }
                />
                Open a disciplinary case about {hrCase.subject?.name}
              </label>
            )}
            {action.openCase && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  id={`case-action-${index}-case-type`}
                  label="Case type"
                  value={caseTypeOf(action)}
                  onChange={(value) => update(index, { caseTypeKey: value })}
                  options={disciplineTypes.map((type) => ({ value: type.key, label: type.label }))}
                  clearable={false}
                />
                <TextArea
                  id={`case-action-${index}-case-summary`}
                  label="Allegation summary"
                  value={action.caseSummary}
                  onChange={(value) => update(index, { caseSummary: value })}
                  required
                  rows={2}
                />
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setActions((list) => list.filter((_, i) => i !== index))}
            >
              Remove action {index + 1}
            </Button>
          </div>
        ))}
        {actions.length < 20 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setActions((list) => [...list, { ...EMPTY_ACTION }])}
          >
            Add action
          </Button>
        )}
      </fieldset>
    </FormShell>
  );
}
